import { computed } from 'vue'
import { useUploadStore } from '@/features/upload/store'
import { useUserStore } from '@/shared/stores/user'
import { useBillingStore } from '@/features/billing/store'
import { useExplorerStore } from '@/features/explorer/store'
import { openModal, closeAllModals, Modals } from '@/shared/dialogs/modals'
import { openSheet, closeAllSheets, Sheets } from '@/shared/dialogs/sheets'
import { useDevice } from '@/shared/composables/useDevice'
import { apolloClient, routerApolloClient } from '@/shared/graphql/client'
import {
  CopyrightStatus,
  FilePermission as RouterFilePermission,
  FileType as RouterFileType,
  FileRole,
  FolderByPathDocument,
  type FolderByPathQuery,
  type FolderByPathQueryVariables,
  type Item,
} from '@/shared/graphql/router/graphql'
import {
  CopyrightOwnersNewDocument,
  type CopyrightOwnersNewQuery,
  CreateBatchUploadUrlDocument,
  ErrorCodes,
  FilePrivacy,
  PlanType,
  UserPermission,
  type CreateBatchUploadUrlMutation,
  type CreateBatchUploadUrlMutationVariables,
} from '@/shared/graphql/legacy/generated'
import { getFileType } from '@/shared/utils/file'
import { upsertLocalExplorerItem } from '@/features/explorer/utils/localExplorerItems'
import axios, { type AxiosProgressEvent } from 'axios'
import { ApolloError, type FetchResult } from '@apollo/client/core'

const UPLOAD_BATCH = 100
const RETRY_TIMEOUT_MS = 20_000
const EXPLORER_PAGE_SIZE = 50

type FolderByPathFolder = NonNullable<FolderByPathQuery['folderByPath']>
type FolderByPathEdge = FolderByPathFolder['items']['edges'][number]
type FolderByPathFile = Extract<
  FolderByPathEdge['node'],
  { __typename?: 'File' }
>

const ROUTER_FILE_TYPES: Record<string, RouterFileType> = Object.fromEntries(
  Object.values(RouterFileType).map((type) => [type, type])
)

const toRouterFileType = (type: string): RouterFileType =>
  ROUTER_FILE_TYPES[type] ?? RouterFileType.Generic

const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function useUploadFlow() {
  const uploadStore = useUploadStore()
  const userStore = useUserStore()
  const billingStore = useBillingStore()
  const explorerStore = useExplorerStore()
  const device = useDevice()

  const hasUploadPermission = computed(
    () => userStore.user?.permissions.includes(UserPermission.Upload) ?? false
  )

  const hasProPermission = computed(
    () => userStore.user?.permissions.includes(UserPermission.Pro) ?? false
  )

  const shouldShowUploadPrivacy = computed(
    () => !userStore.user?.hasUploadedFile && hasProPermission.value
  )

  const isAtBundleLimit = computed(() => {
    if (billingStore.billing?.plan.type !== PlanType.Bundle) return false

    const fileLimit = userStore.user?.totalFilesLimit
    if (typeof fileLimit !== 'number') return false

    return (userStore.user?.totalFiles ?? 0) >= fileLimit
  })

  const canStartUpload = computed(
    () => hasUploadPermission.value && !isAtBundleLimit.value
  )

  const canUpload = (additionalFilesCount?: number) => {
    if (userStore.user?.showUpdatedTerms) return false

    if (isAtBundleLimit.value) {
      openModal(Modals.UpgradeBundleLimit, {
        props: { willReachLimit: false, hasReachedLimit: true },
      })

      return false
    }

    if (hasProPermission.value) return true

    const currentFilesCount = userStore.user?.totalFiles || 0
    const fileLimit = userStore.user?.totalFilesLimit

    const isAtLimit = currentFilesCount === fileLimit
    const willExceedLimit =
      fileLimit && currentFilesCount + (additionalFilesCount || 0) > fileLimit

    if (isAtLimit || willExceedLimit) {
      const dialogName =
        billingStore.billing?.plan.type === PlanType.Bundle
          ? Modals.UpgradeBundleLimit
          : Modals.UpgradeToPro

      openModal(dialogName, {
        props: {
          willReachLimit: !!willExceedLimit,
          hasReachedLimit: isAtLimit,
        },
      })

      return false
    }

    return true
  }

  const showUploadDialog = () => {
    if (!hasUploadPermission.value) return
    if (!canUpload()) return

    openFilePicker()
  }

  const openFilePicker = (accept?: string, capture?: string) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    if (accept) input.accept = accept
    if (capture) input.setAttribute('capture', capture)

    if (device.isMobile.value) {
      input.style.position = 'absolute'
      input.style.appearance = 'none'
      input.style.right = '99999px'
      document.body.append(input)
    }

    input.click()

    input.onchange = (event: Event) => {
      if (device.isMobile.value) input.remove()

      const files = (event.target as HTMLInputElement).files
      if (!files?.length) return

      onFilesPicked(Array.from(files))
    }
  }

  const uploadFiles = (files: FileList) => {
    onFilesPicked(Array.from(files))
  }

  const onFilesPicked = (files: File[]) => {
    if (!hasUploadPermission.value) return
    if (!canUpload(files.length)) return

    if (device.isMobile.value) {
      startMobileRegistration(files)
    } else {
      startDesktopUpload(files)
    }
  }

  const startMobileRegistration = (files: File[]) => {
    const userOrigin = userStore.user?.origin
    const currentFolder = explorerStore.currentFolder

    uploadStore.initDraft({
      files,
      copyrightOwnerId: explorerStore.selectedCopyrightOwner?.id,
      folderId: currentFolder?.id,
      folderName: currentFolder?.name === '/' ? undefined : currentFolder?.name,
      isPrivate:
        hasProPermission.value &&
        userStore.user?.defaultFilePrivacy === FilePrivacy.Private,
      originIds:
        userOrigin && userOrigin !== 'Dacr' ? ['Dacr', userOrigin] : ['Dacr'],
    })

    openRegistrationHub()
  }

  const openUploadPrivacy = (onSuccess: () => void) => {
    openModal(Modals.UploadPrivacy, {
      events: { success: onSuccess },
    })
  }

  const openRegistrationHub = () => {
    closeAllSheets()
    openSheet(Sheets.CopyrightRegistration, {
      events: { success: openConfirmation },
    })
  }

  const openConfirmation = () => {
    openSheet(Sheets.UploadConfirm, {
      events: { success: handleMobileUploadConfirmed },
    })
  }

  const handleMobileUploadConfirmed = () => {
    if (shouldShowUploadPrivacy.value) {
      closeAllSheets()
      openUploadPrivacy(commitUpload)
      return
    }

    commitUpload()
  }

  const commitUpload = () => {
    const draft = uploadStore.draft
    if (!draft) return

    handleFiles(draft.files, {
      folderId: draft.folderId,
      copyrightOwnerId: draft.copyrightOwnerId,
      originIds: draft.originIds,
      isPrivate: draft.isPrivate,
    })

    uploadStore.clearDraft()
    closeAllSheets()
  }

  const startDesktopUpload = (files: File[]) => {
    const openConfirm = () =>
      openModal(Modals.ConfirmUpload, {
        props: { uploadCount: files.length },
        events: {
          success: (originIds) =>
            handleDesktopUploadConfirmed(files, originIds as string[]),
        },
      })

    openConfirm()
  }

  const handleDesktopUploadConfirmed = (files: File[], originIds: string[]) => {
    if (shouldShowUploadPrivacy.value) {
      openUploadPrivacy(() => commitDesktopUpload(files, originIds))
      return
    }

    commitDesktopUpload(files, originIds)
  }

  const commitDesktopUpload = (files: File[], originIds: string[]) => {
    handleFiles(files, { originIds })
    closeAllModals()
  }

  const handleFiles = async (
    fileList: File[],
    options?: {
      folderId?: string
      copyrightOwnerId?: string
      originIds?: string[]
      isPrivate?: boolean
    }
  ) => {
    uploadStore.showUploadBar = true
    uploadStore.startNewSession()

    const targetFolderId = options?.folderId || explorerStore.currentFolder?.id
    const copyrightOwnerId =
      options?.copyrightOwnerId ?? explorerStore.selectedCopyrightOwner?.id
    const cachedOwners = apolloClient.cache.readQuery<CopyrightOwnersNewQuery>({
      query: CopyrightOwnersNewDocument,
    })?.copyrightOwnersNew.items
    const selectedOwner =
      cachedOwners?.find((owner) => owner.id === copyrightOwnerId) ??
      (explorerStore.selectedCopyrightOwner?.id === copyrightOwnerId
        ? explorerStore.selectedCopyrightOwner
        : undefined)
    const optimisticOwner: FolderByPathFile['owner'] | undefined = selectedOwner
      ? {
          __typename: 'CopyrightOwner',
          id: selectedOwner.id,
          name: selectedOwner.name,
          // Legacy selection data only identifies the linked account; the file
          // response fills the organization reference once registration completes.
          referenceId:
            selectedOwner.type === 'ACCOUNT' ? (userStore.user?.id ?? '') : '',
          avatar: { __typename: 'Image', url: selectedOwner.avatarUrl },
        }
      : undefined
    const files = Array.from(fileList)
    const newUploadIds: string[] = []

    files.forEach((file) => {
      const _id = crypto.randomUUID()
      newUploadIds.push(_id)

      uploadStore.addUpload({
        _id,
        timeLeft: 0,
        percentage: 0,
        fileName: file.name,
        abortController: new AbortController(),
        size: file.size,
      })
    })

    for (const batch of chunk(newUploadIds, UPLOAD_BATCH)) {
      const filesToUpload = files.splice(0, UPLOAD_BATCH)

      let response: FetchResult<CreateBatchUploadUrlMutation>

      const getUploadUrls = async () => {
        try {
          response = await apolloClient.mutate<
            CreateBatchUploadUrlMutation,
            CreateBatchUploadUrlMutationVariables
          >({
            mutation: CreateBatchUploadUrlDocument,
            variables: {
              input: {
                parentFolderId: targetFolderId || 'root',
                files: filesToUpload.map((file) => ({
                  fileName: file.name,
                  copyrightOwnerId,
                  fileSize: file.size.toString(),
                  recognizedOriginIds: options?.originIds,
                  isPrivate: options?.isPrivate,
                })),
              },
            },
          })
        } catch (error) {
          console.error(error)

          if ((error as Error).message.includes('rate limit')) {
            await delay(RETRY_TIMEOUT_MS)
            await getUploadUrls()
          } else {
            const errorCode =
              error instanceof ApolloError
                ? error.graphQLErrors
                    .map(({ extensions }) => extensions?.code)
                    .find((code): code is ErrorCodes =>
                      Object.values(ErrorCodes).includes(code as ErrorCodes)
                    )
                : undefined

            batch.forEach((id) => {
              uploadStore.setUploadError(id, {
                error: errorCode ?? ErrorCodes.UploadError,
              })
            })

            throw new Error('Error when uploading')
          }
        }
      }

      try {
        await getUploadUrls()
      } catch {
        continue
      }

      batch.forEach(async (id, index) => {
        const upload = uploadStore.uploads.find((u) => u._id === id)
        const uploadData = response.data!.createBatchUploadUrl.uploads[index]

        if (!upload) return

        upload.id = uploadData.headers.key.split('/')[1]

        const { __typename, ...headers } = uploadData.headers

        const formData = new FormData()

        Object.entries(headers)
          .filter(([, value]) => !!value)
          .forEach(([key, value]) => {
            formData.append(key.replace(/_/g, '-'), value!)
          })

        formData.append('file', filesToUpload[index])

        try {
          await axios.post(uploadData.url, formData, {
            signal: upload.abortController.signal,
            onUploadProgress: (event: AxiosProgressEvent) => {
              const percentage = Math.round((event.progress || 0) * 100)

              uploadStore.updateUploadProgress(upload._id, {
                percentage,
                timeLeft: Math.round(event.estimated || 0),
              })
            },
          })

          if (optimisticOwner) {
            addFileToExplorerCache(
              targetFolderId,
              upload,
              filesToUpload[index],
              optimisticOwner
            )
          }
        } catch (error) {
          console.error(`[Upload] ${(error as Error).message}`)

          uploadStore.setUploadError(upload._id, {
            error: ErrorCodes.UploadError,
          })
        }
      })
    }
  }

  const addFileToExplorerCache = (
    folderId: string | undefined,
    upload: { id?: string; fileName: string },
    file: File,
    owner: FolderByPathFile['owner']
  ) => {
    if (!folderId || !upload.id) return
    const uploadId = upload.id

    const uploadedFile = createUploadedExplorerFile(
      { id: uploadId, fileName: upload.fileName },
      file,
      owner
    )
    upsertLocalExplorerItem(folderId, uploadedFile as unknown as Item)

    const activeQueryVariables = getActiveFolderQueryVariables(folderId)
    if (!activeQueryVariables) return

    routerApolloClient.cache.updateQuery<
      FolderByPathQuery,
      FolderByPathQueryVariables
    >(
      {
        query: FolderByPathDocument,
        variables: activeQueryVariables,
      },
      (response) => {
        if (!response?.folderByPath) return response

        const existingEdges = response.folderByPath.items.edges
        const withoutUploadedFile = existingEdges.filter(
          (edge) => edge.node.id !== uploadId
        )
        const insertedFile = withoutUploadedFile.length === existingEdges.length
        const uploadedEdge: FolderByPathEdge = {
          __typename: 'ItemEdge',
          cursor: uploadId,
          node: uploadedFile,
        }

        return {
          ...response,
          folderByPath: {
            ...response.folderByPath,
            totalFiles: insertedFile
              ? response.folderByPath.totalFiles + 1
              : response.folderByPath.totalFiles,
            items: {
              ...response.folderByPath.items,
              totalFiles: insertedFile
                ? response.folderByPath.items.totalFiles + 1
                : response.folderByPath.items.totalFiles,
              pageInfo: response.folderByPath.items.pageInfo,
              edges: [uploadedEdge, ...withoutUploadedFile],
            },
          },
        }
      }
    )
  }

  const getActiveFolderQueryVariables = (
    folderId: string
  ): FolderByPathQueryVariables | undefined => {
    const currentFolder = explorerStore.currentFolder
    if (!currentFolder || currentFolder.id !== folderId) return

    return {
      path: currentFolder.path,
      sort: explorerStore.sortBy,
      first: EXPLORER_PAGE_SIZE,
    }
  }

  const createUploadedExplorerFile = (
    upload: { id: string; fileName: string },
    file: File,
    owner: FolderByPathFile['owner']
  ): FolderByPathFile => {
    const user = userStore.user
    const userId = user?.id ?? ''
    const userName = user?.name ?? ''

    return {
      __typename: 'File',
      id: upload.id,
      name: upload.fileName,
      type: toRouterFileType(getFileType(upload.fileName, file.type)),
      description: null,
      role: FileRole.Owner,
      permissions: [
        RouterFilePermission.Archive,
        RouterFilePermission.Download,
        RouterFilePermission.Edit,
        RouterFilePermission.Invite,
        RouterFilePermission.Privacy,
        RouterFilePermission.View,
      ],
      copyright: {
        __typename: 'FileCopyright',
        id: null,
        status: CopyrightStatus.Processing,
        registrations: [],
      },
      metadata: {
        __typename: 'FileMetadata',
        originalFilename: upload.fileName,
        size: file.size.toString(),
      },
      asset: null,
      settings: {
        __typename: 'FileSettings',
        isPrivate: user?.defaultFilePrivacy === FilePrivacy.Private,
      },
      registrant: {
        __typename: 'User',
        id: userId,
        name: userName,
      },
      owner,
      coOwners: {
        __typename: 'CoOwnersConnection',
        totalCount: 0,
        edges: [],
      },
      authors: {
        __typename: 'AuthorsConnection',
        totalCount: 0,
        edges: [],
      },
      viewers: {
        __typename: 'ViewersConnection',
        totalCount: 0,
        edges: [],
      },
    }
  }

  return {
    hasUploadPermission,
    isAtBundleLimit,
    canStartUpload,
    showUploadDialog,
    uploadFiles,
    openFilePicker,
  }
}
